/**
 * Trending-detection helpers + 🔥 badge component.
 *
 * "Hot" vs "Trending" thresholds are deliberately tuned for an early-stage
 * platform: low enough that real engagement gets recognised, high enough
 * that a single re-play doesn't fake it.
 *
 *   plays >= 25   → 🔥          ("hot")
 *   plays >= 250  → 🔥🔥          ("trending")
 *   plays >= 2500 → 🔥🔥🔥        ("blazing")
 *
 * A caller can force the badge by setting any of these on the item:
 *   _trending: true      → at least 🔥🔥
 *   _hot: true           → at least 🔥
 *   _trendingLevel: 1|2|3
 *
 * Used as:
 *   <FireBadge item={track} />
 *   <FireBadge level={2} />              (when you know the level)
 */
import React from "react";

export function getTrendingLevel(item) {
  if (!item) return 0;
  if (typeof item._trendingLevel === "number") {
    return Math.max(0, Math.min(3, item._trendingLevel));
  }
  if (item._trending === true) return 2;
  if (item._hot === true) return 1;
  // Plays-driven heuristic. Works on:
  //   - uploaded MP3s (track.plays)
  //   - catalog rows (track.weekly_plays / track.all_time_plays / track.total_plays)
  //   - artist cards (artist.total_plays / artist.weekly_plays)
  const plays =
    Number(
      item.weekly_plays ??
        item.weeklyPlays ??
        item.all_time_plays ??
        item.allTimePlays ??
        item.total_plays ??
        item.totalPlays ??
        item.plays ??
        0,
    ) || 0;
  if (plays >= 2500) return 3;
  if (plays >= 250) return 2;
  if (plays >= 25) return 1;
  return 0;
}

const LEVEL_LABEL = {
  1: "Hot",
  2: "Trending",
  3: "Blazing",
};

/**
 * Compact 🔥 emoji badge — brutalist pill style. High-contrast vermilion
 * wash with thick uppercase label, optimised for visibility against album
 * artwork. Pass ``level`` directly when you know it (e.g. from a charts
 * rank position) — otherwise pass ``item`` and we derive it from play counts.
 */
export const FireBadge = ({ item, level, size = "sm", showLabel = false, className = "" }) => {
  const resolved = typeof level === "number" ? level : getTrendingLevel(item);
  if (resolved <= 0) return null;
  const fires = "🔥".repeat(resolved);
  const sizeCls =
    size === "lg"
      ? "text-xs px-2.5 py-1 gap-1.5"
      : size === "md"
      ? "text-[11px] px-2 py-1 gap-1.5"
      : "text-[10px] px-1.5 py-0.5 gap-1";
  return (
    <span
      data-testid={`fire-badge-${resolved}`}
      title={LEVEL_LABEL[resolved] || "Trending"}
      aria-label={`${LEVEL_LABEL[resolved] || "Trending"} on 1o1sound`}
      className={`inline-flex items-center font-black uppercase tracking-[0.18em] rounded-sm bg-[#FF2E00]/10 border border-[#FF2E00]/30 text-[#FF2E00] backdrop-blur-sm whitespace-nowrap ${sizeCls} ${className}`}
    >
      <span className="select-none animate-[fire-pulse_1.6s_ease-in-out_infinite] leading-none">{fires}</span>
      {(showLabel || resolved === 3) && (
        <span className="leading-none">{LEVEL_LABEL[resolved]}</span>
      )}
    </span>
  );
};

export default FireBadge;
